import { useState, useEffect, useRef } from 'react'
import { useInfiniteQuery, useQuery } from 'react-query'
import { Link, useSearchParams, useNavigate, useParams } from 'react-router-dom'
import { getImageUrl } from '../utils/apiUrl'
import { getProductUrl } from '../utils/urlUtils'
import { 
  Search, 
  Filter, 
  Calendar, 
  Euro, 
  Package, 
  Clock,
  Star,
  Eye,
  ShoppingCart,
  X,
  ClipboardList,
  ChevronUp
} from 'lucide-react'
import { apiService as api } from '../services/api'
import noProductImage from '../assets/no_product.png'
import ProductImage from '../components/ProductImage'
import AddToFavorites from '../components/AddToFavorites'
import { useCatalogSettings } from '../contexts/CatalogContext'
import { stripHtmlTags, truncateText } from '../utils/htmlUtils'
import toast from 'react-hot-toast'
import { useCart } from '../contexts/CartContext'

const RentmanCatalogue = () => {
  const { settings: catalogSettings } = useCatalogSettings()
  const [user, setUser] = useState(null)
  const [showScrollTop, setShowScrollTop] = useState(false)
  const navigate = useNavigate()
  
  // Gérer l'affichage du bouton scroll to top
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300)
    }
    
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Fonction pour remonter en haut
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Charger l'utilisateur
  useEffect(() => {
    const token = localStorage.getItem('user_token');
    if (token) {
      api.get('/auth/me', { params: { t: Date.now() } })
        .then(res => setUser(res.data?.user || null))
        .catch(() => setUser(null));
    }
  }, []);
  
  // Charger réglages admin (pour Safari ou multi-navigateurs)
  const { data: adminSettingsData } = useQuery(
    'rentman-settings-public',
    () => api.get('/rentman/settings/public', { params: { t: Date.now() } }).then(res => res.data),
    {
      staleTime: 0,
      cacheTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: true
    }
  )
  const backendShowPrice = adminSettingsData?.show_regular_price
  const backendShowAvailability = adminSettingsData?.show_availability
  const backendShowDescription = adminSettingsData?.show_product_description
  const backendShowAvailabilityFilter = adminSettingsData?.show_availability_filter

  // Source de vérité: réglages publics backend (appliqués à tous les utilisateurs)
  const showPrice = backendShowPrice === true
  const showAvailability = backendShowAvailability === true
  const showDescription = backendShowDescription === true || typeof backendShowDescription === 'undefined'
  const showAvailabilityFilter = backendShowAvailabilityFilter === true || typeof backendShowAvailabilityFilter === 'undefined'
  const { addToCart } = useCart()
  const [zoomImageUrl, setZoomImageUrl] = useState(null)
  const [searchParams] = useSearchParams()
  const { categorySlug, subCategorySlug } = useParams()
  
  // Mapping des slugs vers les IDs de catégories
  const categorySlugToId = {
    'cameras': 'images',
    'regie': 'regis', 
    'energie': 'energie',
    'eclairage': 'lumiere',
    'machinerie': 'machinerie',
    'audio': 'audio'
  }
  
  // Mapping inverse : IDs vers slugs
  const categoryIdToSlug = {
    'images': 'cameras',
    'regis': 'regie',
    'energie': 'energie', 
    'lumiere': 'eclairage',
    'machinerie': 'machinerie',
    'audio': 'audio'
  }
  
  // Fonction pour générer l'URL de catégorie avec slug
  const getCategoryUrl = (categoryId) => {
    const slug = categoryIdToSlug[categoryId]
    return slug ? `/catalogue-materiels/${slug}` : `/catalogue-materiels?parent_category_id=${categoryId}`
  }
  
  // Fonction pour créer un slug à partir d'un nom de catégorie
  const createSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Supprimer les accents
      .replace(/[^a-z0-9\s-]/g, '') // Garder seulement lettres, chiffres, espaces et tirets
      .replace(/\s+/g, '-') // Remplacer les espaces par des tirets
      .replace(/-+/g, '-') // Remplacer les tirets multiples par un seul
      .trim()
  }
  
  // Fonction pour trouver une catégorie par son slug
  const findCategoryBySlug = (slug, categories) => {
    return categories.find(cat => createSlug(cat.name) === slug)
  }
  
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    category_id: '',
    sub_sub_category_id: '',
    parent_category_id: categorySlug ? categorySlugToId[categorySlug] || '' : '',
    available: '', // Pas de filtre par défaut sur la disponibilité
    page: 1
  })
  
  // État pour éviter les requêtes pendant les changements de filtres
  const [isFiltersReady, setIsFiltersReady] = useState(false)

  // Mettre à jour le filtre de recherche quand l'URL change (ex: /catalogue-materiels?search=sony)
  useEffect(() => {
    const s = (searchParams.get('search') || '').trim()
    setFilters(prev => ({ ...prev, search: s, page: 1 }))
    setIsFiltersReady(true)
  }, [searchParams])

  // Mettre à jour le filtre de catégorie quand le slug change
  useEffect(() => {
    if (categorySlug) {
      const categoryId = categorySlugToId[categorySlug]
      if (categoryId) {
        setFilters(prev => ({ 
          ...prev, 
          parent_category_id: categoryId,
          category_id: '',
          sub_sub_category_id: '',
          page: 1 
        }))
        setIsFiltersReady(true)
      }
    }
  }, [categorySlug])

  // Récupérer les catégories
  const { data: categoriesData, isLoading: categoriesLoading, error: categoriesError } = useQuery(
    'rentman-categories',
    () => {
      // console.log('📂 Fetching categories...')
      return api.get('/rentman/categories').then(res => {
        // console.log('📂 Categories response:', res.data)
        // console.log('📂 Categories count:', res.data?.categories?.length || 0)
        return res.data
      })
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      onSuccess: (data) => {
        // console.log('📂 Categories loaded successfully:', data?.categories?.length || 0, 'categories')
        // Marquer les filtres comme prêts une fois les catégories chargées
        if (!isFiltersReady) {
          setIsFiltersReady(true)
        }
      },
      onError: (error) => {
        console.error('📂 Error loading categories:', error)
      }
    }
  )

  // console.log('Categories data:', categoriesData)
  // console.log('Categories loading:', categoriesLoading)
  // console.log('Categories error:', categoriesError)

  // Mettre à jour le filtre de sous-catégorie quand le subCategorySlug change
  useEffect(() => {
    if (subCategorySlug && categoriesData?.categories) {
      const allCategories = categoriesData.categories
      const subCategory = findCategoryBySlug(subCategorySlug, allCategories)
      if (subCategory) {
        setFilters(prev => ({ 
          ...prev, 
          category_id: subCategory.id,
          sub_sub_category_id: '',
          page: 1 
        }))
        setIsFiltersReady(true)
      }
    }
  }, [subCategorySlug, categoriesData])

  // Gérer les paramètres de requête avec slugs (pour les sous-sous-catégories et autres)
  useEffect(() => {
    if (!categoriesData || !Array.isArray(categoriesData.categories)) return
    
    const allCategories = categoriesData.categories
    let hasChanges = false
    const newFilters = { ...filters }
    
    // Gérer le paramètre 'sub_category' (sous-sous-catégorie par slug)
    const subCategorySlug = searchParams.get('sub_category')
    if (subCategorySlug && !newFilters.sub_sub_category_id) {
      const subCategory = findCategoryBySlug(subCategorySlug, allCategories)
      if (subCategory) {
        newFilters.sub_sub_category_id = subCategory.id
        hasChanges = true
      }
    }
    
    // Gérer le paramètre 'parent_category' (catégorie parente par slug - fallback)
    const parentCategorySlug = searchParams.get('parent_category')
    if (parentCategorySlug && !newFilters.parent_category_id) {
      const parentCategory = findCategoryBySlug(parentCategorySlug, allCategories)
      if (parentCategory) {
        newFilters.parent_category_id = parentCategory.id
        hasChanges = true
      }
    }
    
    if (hasChanges) {
      setFilters(prev => ({ ...prev, ...newFilters, page: 1 }))
      setIsFiltersReady(true)
    }
  }, [searchParams, categoriesData])

  // Pré-calcul des enfants pour conditionner l'affichage
  const allCategories = categoriesData?.categories || []
  const childrenOfParent = filters.parent_category_id
    ? allCategories.filter(c => String(c.parent_id) === String(filters.parent_category_id))
    : []
  const childrenOfSub = filters.category_id
    ? allCategories.filter(c => String(c.parent_id) === String(filters.category_id))
    : []

  // Infinite scroll helpers
  const buildParams = () => {
    const params = { ...filters }
    // console.log('🔧 buildParams - Filtres initiaux:', filters)
    // console.log('🔧 buildParams - Categories disponibles:', categoriesData?.categories?.length || 0)
    
    if (categoriesData?.categories) {
      const byParent = new Map()
      for (const c of categoriesData.categories) {
        const pid = c.parent_id == null ? null : String(c.parent_id)
        if (!byParent.has(pid)) byParent.set(pid, [])
        byParent.get(pid).push(c)
      }
      const collect = (parentId, acc) => {
        const pid = parentId == null ? null : String(parentId)
        const children = byParent.get(pid) || []
        for (const child of children) {
          acc.add(child.id)
          collect(child.id, acc)
        }
        return acc
      }
      
      if (filters.parent_category_id && !filters.category_id) {
        const allSet = collect(filters.parent_category_id, new Set([filters.parent_category_id]))
        params.category_ids = Array.from(allSet).join(',')
        // console.log('🔧 buildParams - Catégorie parente sélectionnée:', filters.parent_category_id, 'IDs inclus:', params.category_ids)
      } else if (filters.category_id && !filters.sub_sub_category_id) {
        const allSet = collect(filters.category_id, new Set([filters.category_id]))
        params.category_ids = Array.from(allSet).join(',')
        delete params.category_id
        // console.log('🔧 buildParams - Sous-catégorie sélectionnée:', filters.category_id, 'IDs inclus:', params.category_ids)
      } else if (filters.sub_sub_category_id) {
        params.category_id = filters.sub_sub_category_id
        delete params.category_ids
        // console.log('🔧 buildParams - Sous-sous-catégorie sélectionnée:', filters.sub_sub_category_id)
      }
    }
    
    // console.log('🔧 buildParams - Paramètres finaux:', params)
    return params
  }

  const {
    data: productsPages,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery(
    ['rentman-products', filters],
    async ({ pageParam = 1 }) => {
      const params = { ...buildParams(), page: pageParam, limit: 24 }
      // console.log('🔍 Requête produits avec params:', params)
      const res = await api.get('/rentman/products', { 
        params: { ...params, t: Date.now() } // Cache-busting
      })
      // console.log('📦 Réponse produits:', res.data)
      return res.data
    },
    {
      enabled: isFiltersReady && !categoriesLoading, // Ne pas exécuter si les filtres ne sont pas prêts
      getNextPageParam: (lastPage) => {
        const p = lastPage?.pagination
        if (!p) return undefined
        return p.page < p.pages ? p.page + 1 : undefined
      },
      keepPreviousData: false, // Ne pas garder les données précédentes pour éviter les états incohérents
      staleTime: 30 * 1000, // Réduire le staleTime pour des mises à jour plus fréquentes
      refetchOnWindowFocus: false // Éviter les refetch automatiques
    }
  )

  const products = (productsPages?.pages || []).flatMap(p => p.products || [])
  const loadMoreRef = useRef(null)
  const initializedFromQueryRef = useRef(false)

  // Initialiser depuis ?category_id= si présent
  useEffect(() => {
    if (initializedFromQueryRef.current) return
    if (!categoriesData || !Array.isArray(categoriesData.categories)) return
    const cid = searchParams.get('category_id')
    if (!cid) return
    const all = categoriesData.categories
    // Essayer d'abord par id interne, sinon par rentman_id
    let target = all.find(c => String(c.id) === String(cid))
    if (!target) {
      target = all.find(c => String(c.rentman_id) === String(cid))
    }
    if (!target) return
    // Déterminer la hiérarchie: root / child / sub-child
    const parent = target.parent_id ? all.find(c => String(c.id) === String(target.parent_id)) : null
    const grandParent = parent && parent.parent_id ? all.find(c => String(c.id) === String(parent.parent_id)) : null
    if (!parent) {
      // Catégorie racine
      setFilters(prev => ({ ...prev, parent_category_id: String(target.id), category_id: '', sub_sub_category_id: '', page: 1 }))
    } else if (parent && !grandParent) {
      // Niveau 2
      setFilters(prev => ({ ...prev, parent_category_id: String(parent.id), category_id: String(target.id), sub_sub_category_id: '', page: 1 }))
    } else if (grandParent) {
      // Niveau 3
      setFilters(prev => ({ ...prev, parent_category_id: String(grandParent.id), category_id: String(parent.id), sub_sub_category_id: String(target.id), page: 1 }))
    }
    initializedFromQueryRef.current = true
  }, [searchParams, categoriesData])

  useEffect(() => {
    const el = loadMoreRef.current
    if (!el) return
    if (!hasNextPage) return
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const handleFilterChange = (key, value) => {
    setIsFiltersReady(false) // Marquer les filtres comme non prêts
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset page when filters change
    }))
    // Marquer les filtres comme prêts après un court délai
    setTimeout(() => {
      setIsFiltersReady(true)
    }, 100)
  }

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }))
  }

  // Fonction pour mettre à jour l'URL avec les filtres
  const updateURL = (newFilters) => {
    const params = new URLSearchParams()
    
    // Si on a une catégorie parente, utiliser le slug
    if (newFilters.parent_category_id) {
      const slug = categoryIdToSlug[newFilters.parent_category_id]
      if (slug) {
        // Utiliser l'URL avec slug pour les catégories principales
        let newURL = `/catalogue-materiels/${slug}`
        
        // Ajouter les sous-catégories dans le chemin si possible
        if (newFilters.category_id) {
          // Essayer de trouver le slug de la sous-catégorie
          const subCategory = allCategories.find(c => String(c.id) === String(newFilters.category_id))
          if (subCategory) {
            const subSlug = createSlug(subCategory.name)
            newURL += `/${subSlug}`
            
            // Ajouter les sous-sous-catégories comme paramètres si nécessaire
            if (newFilters.sub_sub_category_id) {
              const subSubCategory = allCategories.find(c => String(c.id) === String(newFilters.sub_sub_category_id))
              if (subSubCategory) {
                const subSubSlug = createSlug(subSubCategory.name)
                params.set('sub_category', subSubSlug)
              } else {
                params.set('sub_sub_category_id', newFilters.sub_sub_category_id)
              }
            }
          } else {
            // Fallback : utiliser les paramètres de requête
            params.set('category_id', newFilters.category_id)
            if (newFilters.sub_sub_category_id) {
              params.set('sub_sub_category_id', newFilters.sub_sub_category_id)
            }
          }
        } else if (newFilters.sub_sub_category_id) {
          // Si on a seulement une sous-sous-catégorie, l'ajouter comme paramètre
          const subSubCategory = allCategories.find(c => String(c.id) === String(newFilters.sub_sub_category_id))
          if (subSubCategory) {
            const subSubSlug = createSlug(subSubCategory.name)
            params.set('sub_category', subSubSlug)
          } else {
            params.set('sub_sub_category_id', newFilters.sub_sub_category_id)
          }
        }
        
        // Ajouter les autres paramètres
        if (newFilters.search) {
          params.set('search', newFilters.search)
        }
        if (newFilters.available) {
          params.set('available', newFilters.available)
        }
        
        const queryString = params.toString()
        if (queryString) {
          newURL += `?${queryString}`
        }
        
        navigate(newURL, { replace: true })
        return
      }
    }
    
    // Fallback : utiliser les paramètres de requête pour les autres cas
    if (newFilters.parent_category_id) {
      // Essayer de trouver le slug de la catégorie parente
      const parentCategory = allCategories.find(c => String(c.id) === String(newFilters.parent_category_id))
      if (parentCategory) {
        const parentSlug = createSlug(parentCategory.name)
        params.set('parent_category', parentSlug)
      } else {
        params.set('parent_category_id', newFilters.parent_category_id)
      }
    }
    if (newFilters.category_id) {
      // Essayer de trouver le slug de la sous-catégorie
      const subCategory = allCategories.find(c => String(c.id) === String(newFilters.category_id))
      if (subCategory) {
        const subSlug = createSlug(subCategory.name)
        params.set('category', subSlug)
      } else {
        params.set('category_id', newFilters.category_id)
      }
    }
    if (newFilters.sub_sub_category_id) {
      // Essayer de trouver le slug de la sous-sous-catégorie
      const subSubCategory = allCategories.find(c => String(c.id) === String(newFilters.sub_sub_category_id))
      if (subSubCategory) {
        const subSubSlug = createSlug(subSubCategory.name)
        params.set('sub_category', subSubSlug)
      } else {
        params.set('sub_sub_category_id', newFilters.sub_sub_category_id)
      }
    }
    if (newFilters.search) {
      params.set('search', newFilters.search)
    }
    if (newFilters.available) {
      params.set('available', newFilters.available)
    }
    
    // Construire la nouvelle URL
    const newURL = params.toString() ? `/catalogue-materiels?${params.toString()}` : '/catalogue-materiels'
    navigate(newURL, { replace: true })
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price)
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Package className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Erreur de chargement</h2>
          <p className="text-gray-600">Impossible de charger le catalogue</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container-custom py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-4">
            <span className="text-gradient">Catalogue</span>
          </h1>
          <p className="text-xl text-gray-600">
            Matériel professionnel
          </p>
        </div>

        {/* Zone filtres compacte */}
        <div className="card mb-4">
          {!isFiltersReady && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                <span className="text-sm text-blue-700">Mise à jour des filtres...</span>
              </div>
            </div>
          )}

          {/* Ligne 1: (Disponibilité si autorisé) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">

            {(showAvailabilityFilter && showAvailability) ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Disponibilité</label>
                <select
                  value={String(filters.available)}
                  onChange={(e) => handleFilterChange('available', e.target.value === 'true')}
                  className="input-field"
                >
                  <option value="">Tous</option>
                  <option value="true">Disponible</option>
                  <option value="false">Indisponible</option>
                </select>
              </div>
            ) : (
              <div className="hidden md:block"></div>
            )}

            
          </div>

          {/* Ligne 2: Catégorie + Sous + Sous-sous */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4 mt-2 md:mt-0">
            <div>
              <select
                key={`parent-${filters.parent_category_id || 'all'}`}
                value={filters.parent_category_id || ''}
                onChange={(e) => {
                  const parentId = e.target.value
                  const newFilters = { 
                    parent_category_id: parentId, 
                    category_id: '', 
                    sub_sub_category_id: '', 
                    search: '', // Effacer la recherche
                    page: 1 
                  }
                  setIsFiltersReady(false)
                  setFilters(prev => ({ ...prev, ...newFilters }))
                  updateURL(newFilters)
                  setTimeout(() => {
                    setIsFiltersReady(true)
                  }, 100)
                }}
                className="input-field"
                disabled={categoriesLoading}
              >
                <option value="">
                  {categoriesLoading ? 'Chargement...' : 'Toutes les catégories'}
                </option>
                {categoriesData?.categories?.filter(c => c.parent_id === null)
                  .sort((a, b) => {
                    // Ordre de priorité des catégories parentes
                    const order = {
                      '2325': 1,  // Image
                      '2327': 2,  // Lumière
                      '2326': 3,  // Machinerie
                      '2320': 4,  // Energie
                      '2323': 5,  // La Régie
                      '2321': 6   // Audio
                    };
                    const orderA = order[a.id] || 999;
                    const orderB = order[b.id] || 999;
                    return orderA - orderB;
                  })
                  .map((category) => {
                    const slug = categoryIdToSlug[category.id] || createSlug(category.name)
                    return (
                      <option key={category.id} value={category.id} data-slug={slug}>
                        {category.name}
                      </option>
                    )
                  })}
              </select>
              {categoriesError && (
                <p className="text-xs text-red-500 mt-1">Erreur de chargement des catégories</p>
              )}
            </div>

            <div>
              {filters.parent_category_id && childrenOfParent.length > 0 && (
                <>
                  <select
                    key={`child-${filters.category_id || 'all'}`}
                    value={filters.category_id}
                    onChange={(e) => {
                      const newFilters = { 
                        category_id: e.target.value, 
                        sub_sub_category_id: '', 
                        search: '', // Effacer la recherche
                        page: 1 
                      }
                      setIsFiltersReady(false)
                      setFilters(prev => ({ ...prev, ...newFilters }))
                      updateURL(newFilters)
                      setTimeout(() => {
                        setIsFiltersReady(true)
                      }, 100)
                    }}
                    className="input-field"
                    disabled={categoriesLoading}
                  >
                    <option value="">Toutes</option>
                    {childrenOfParent.map((category) => {
                      const slug = createSlug(category.name)
                      return (
                        <option key={category.id} value={category.id} data-slug={slug}>
                          {category.name}
                        </option>
                      )
                    })}
                  </select>
                </>
              )}
            </div>

            <div>
              {filters.category_id && childrenOfSub.length > 0 && (
                <>
                  <select
                    key={`sub-${filters.sub_sub_category_id || 'all'}`}
                    value={filters.sub_sub_category_id}
                    onChange={(e) => {
                      const newFilters = { 
                        sub_sub_category_id: e.target.value, 
                        search: '', // Effacer la recherche
                        page: 1 
                      }
                      setIsFiltersReady(false)
                      setFilters(prev => ({ ...prev, ...newFilters }))
                      updateURL(newFilters)
                      setTimeout(() => {
                        setIsFiltersReady(true)
                      }, 100)
                    }}
                    className="input-field"
                    disabled={categoriesLoading}
                  >
                    <option value="">Toutes</option>
                    {childrenOfSub.map((category) => {
                      const slug = createSlug(category.name)
                      return (
                        <option key={category.id} value={category.id} data-slug={slug}>
                          {category.name}
                        </option>
                      )
                    })}
                  </select>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Liste des produits */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-6">
          {isLoading ? (
            // Skeleton loading
            Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="card animate-pulse">
                <div className="h-48 bg-gray-200 rounded-lg mb-4"></div>
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-3 bg-gray-200 rounded mb-4"></div>
                <div className="h-8 bg-gray-200 rounded"></div>
              </div>
            ))
          ) : products?.length > 0 ? (
            products.map((product) => (
              <div key={product.id} className="card hover:shadow-lg transition-shadow group">
                {/* Image du produit */}
                <Link to={getProductUrl(product)} className="block">
                  <div
                    className="h-48 bg-white rounded-lg mb-4 overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={(e) => {
                      e.preventDefault()
                      if (product.image_url) {
                        const full = getImageUrl(product.image_url)
                        setZoomImageUrl(full)
                      }
                    }}
                    role="button"
                    aria-label="Agrandir l'image"
                  >
                    <ProductImage
                      imageUrl={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover"
                      fallbackIcon={Package}
                      fallbackIconSize="h-12 w-12"
                    />
                  </div>
                </Link>

                {/* Informations du produit */}
                <div className="space-y-3">
                  <div>
                    <div className="h-12 flex items-start">
                      <Link to={getProductUrl(product)} className="block w-full">
                        <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2 leading-tight hover:text-logo transition-colors">
                          {product.name}
                        </h3>
                      </Link>
                    </div>
                    {product.category_name && (
                      <p className="text-sm text-gray-600">{product.category_name}</p>
                    )}
                  </div>

                  {showDescription && product.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {truncateText(product.description, 120)}
                    </p>
                  )}

                  <div className="flex items-center justify-between">
                    {showPrice && (
                      <div className="flex items-center space-x-2">
                        <Euro className="h-4 w-4 text-orange-500" />
                        <span className="font-semibold text-gray-900">
                          {formatPrice(product.price_per_day)}
                        </span>
                        <span className="text-sm text-gray-600">/jour</span>
                      </div>
                    )}

                    {showAvailability && (
                      <div className="flex items-center space-x-1">
                        <div className={`w-2 h-2 rounded-full ${
                          product.is_available ? 'bg-green-500' : 'bg-red-500'
                        }`}></div>
                        <span className="text-xs text-gray-600">
                          {product.is_available ? 'Disponible' : 'Indisponible'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="pt-2">
                    {/* Version Mobile: 2 lignes */}
                    <div className="md:hidden space-y-2">
                      {/* Ligne 1: Voir et Liste */}
                      <div className="flex space-x-2">
                        <Link
                          to={getProductUrl(product)}
                          className="btn-outline flex items-center justify-center text-sm px-3 flex-1"
                          title="Voir les détails"
                        >
                          <Eye className="h-4 w-4" />
                          <span className="ml-1">Voir</span>
                        </Link>
                        {user && (
                          <AddToFavorites 
                            productId={product.id} 
                            productName={product.name}
                            className="flex items-center justify-center text-sm px-3 text-orange-500 hover:text-orange-600 transition-colors border border-orange-200 hover:border-orange-300 rounded-md flex-1"
                          />
                        )}
                      </div>
                      
                      {/* Ligne 2: Réserver */}
                      {product.is_available && (
                        <button
                          type="button"
                          onClick={() => {
                            addToCart(product, 1)
                            toast.success('Ajouté à votre liste')
                          }}
                          className="btn-primary w-full flex items-center justify-center text-sm"
                        >
                          <ShoppingCart className="h-4 w-4 mr-1" />
                          Réserver
                        </button>
                      )}
                    </div>

                    {/* Version Desktop: 1 ligne */}
                    <div className="hidden md:flex space-x-2">
                      <Link
                        to={getProductUrl(product)}
                        className="btn-outline flex items-center justify-center text-sm px-3"
                        title="Voir les détails"
                      >
                        <Eye className="h-4 w-4" />
                      </Link>
                      {user && (
                        <AddToFavorites 
                          productId={product.id} 
                          productName={product.name}
                          className="flex items-center justify-center text-sm px-3 text-orange-500 hover:text-orange-600 transition-colors border border-orange-200 hover:border-orange-300 rounded-md"
                        />
                      )}
                      {product.is_available && (
                        <button
                          type="button"
                          onClick={() => {
                            addToCart(product, 1)
                            toast.success('Ajouté à votre liste')
                          }}
                          className="btn-primary flex-1 flex items-center justify-center text-sm"
                        >
                          <ShoppingCart className="h-4 w-4 mr-1" />
                          Réserver
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full py-12">
              <div className="flex items-center justify-center space-x-8">
                <div className="text-right">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Aucun produit trouvé
                  </h3>
                  <p className="text-gray-600">
                    Essayez de modifier vos critères de recherche
                  </p>
                </div>
                <img 
                  src={noProductImage} 
                  alt="Aucun produit trouvé" 
                  className="h-64 w-auto"
                />
              </div>
            </div>
          )}
        </div>

        {/* Infinite sentinel */}
        <div ref={loadMoreRef} className="py-6 text-center">
          {isFetchingNextPage && (
            <span className="text-sm text-gray-500">Chargement…</span>
          )}
          {!hasNextPage && products?.length > 0 && (
            <span className="text-sm text-gray-400">Tous les produits chargés</span>
          )}
        </div>

        {/* Modal Zoom Image */}
        {zoomImageUrl && (
          <div
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setZoomImageUrl(null)}
          >
            <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
              <button
                className="absolute -top-3 -right-3 bg-white rounded-full p-1 shadow hover:bg-gray-100"
                aria-label="Fermer"
                onClick={() => setZoomImageUrl(null)}
              >
                <X className="h-5 w-5 text-gray-700" />
              </button>
              <img src={zoomImageUrl} alt="Aperçu" className="w-full h-auto rounded-lg shadow-lg" />
            </div>
          </div>
        )}

        {/* Bouton Scroll to Top */}
        {showScrollTop && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-6 right-6 bg-orange-500 hover:bg-orange-600 text-white p-3 rounded-full shadow-lg transition-all duration-300 z-40 group"
            aria-label="Remonter en haut"
          >
            <ChevronUp className="h-6 w-6 group-hover:scale-110 transition-transform" />
          </button>
        )}
      </div>
    </div>
  )
}

export default RentmanCatalogue
